import { TJsonQuery } from '../../../../packages/JsonQuery';
import { setAwaitingGeoFocus } from '../../../Map/actions/geoFocus';
import { fetchGeoBbox } from '../../services/geoBbox';
import { TThunkAction } from '../../types/redux';
import { isRequestCancelled } from '../../utils/isRequestCancelled';
import { fetchHybridSearchData } from '../fetchHybridSearchData';
import { focusMapByBbox } from '../mapBounds';
import { fetchRegion } from '../region';

import type { IBBoxSchema } from '../../../../node/types/bbox';

interface IApplyGeoChangeParameters {
  /** Новый jsonQuery из события фильтров: передаётся параметром, а не читается из store,
   * чтобы не зависеть от момента применения justChangeJsonQuery. */
  jsonQuery: TJsonQuery;
}

/**
 * Смена гео из фильтров: лёгким get-bbox узнаём область, фокусируем карту (focusMapByBbox →
 * useBoundsSync), а данные грузит фаза 2 (useGeoFocusRefetch) уже под фактический зум —
 * единственным тяжёлым запросом /map (там же отработают ветки «ничего не найдено»).
 *
 * Флаг isAwaitingGeoFocus взводится ДО запроса: при фолбэке ответ /map сам положит bbox
 * (Succeed + updateBounds) → тот же фокус → та же фаза 2, то есть фолбэк — это прежняя
 * рабочая двухфазная схема целиком. Поэтому флаг при фолбэке не сбрасываем; залипший флаг
 * сбрасывает следующее не-гео изменение фильтров (см. applyMicrofrontendFilters).
 *
 * fetchRegion диспатчится fire-and-forget — как и раньше в applyMicrofrontendFilters.
 */
export const applyGeoChange =
  ({ jsonQuery }: IApplyGeoChangeParameters): TThunkAction<Promise<void>> =>
  async (dispatch, getState, context) => {
    const {
      custom: { subdomain },
      logger,
    } = context;

    dispatch(setAwaitingGeoFocus(true));
    dispatch(fetchRegion());

    let bbox: IBBoxSchema | undefined;
    try {
      bbox = (await fetchGeoBbox(context, { jsonQuery, subdomain })).bbox;
    } catch (error) {
      if (isRequestCancelled(error)) {
        return;
      }

      logger.warning('fetchGeoBbox failed, fallback to updateBounds search', {
        domain: 'actions/applyGeoChange',
      });
    }

    if (bbox?.topLeft && bbox?.bottomRight) {
      dispatch(focusMapByBbox(bbox));

      return;
    }

    dispatch(
      fetchHybridSearchData({
        updateBounds: true,
        deviceType: 'desktop',
        allowNonGeoFallback: false,
      }),
    );
  };
